import { createSlice } from '@reduxjs/toolkit';

const emptyListState = () => ({
  sites: [],
  nextCursor: null,
  listStatus: 'idle',
  listError: null,
});

export const initialState = {
    isSaving: false,
    isFetching: false,
    isFinishedCreating: true,
    ondemandSite: {},
    error: null,
    succeeded: false,
    // Inventory list keyed by product (V1).
    lists: {},
    decommission: {
      status: 'idle',
      sk: null,
      label: null,
      error: null,
    },
};

export const slice = createSlice({
  name: 'ondemand',
  initialState,
  reducers: {
    fetchOndemandRequested: (state) => {
      state.isFetching = true;
      state.error = null;
    },
    fetchOndemandFailed: (state, action) => {
      state.isFetching = false;
      state.error = action.payload;
    },
    fetchOndemandSucceeded: (state, action) => {
      state.isFetching = false;
      state.isFinishedCreating = true;
      state.error = null;
      state.ondemandSite = action.payload;
    },
    createOndemandRequested: (state) => {
      state.isSaving = true;
      state.error = null;
      state.succeeded = false;
    },
    createOndemandFailed: (state, action) => {
      state.isSaving = false;
      state.error = action.payload;
      state.succeeded = false;
      state.isFinishedCreating = true;
    },
    createOndemandSucceeded: (state, action) => {
      state.isSaving = false;
      state.error = null;
      state.succeeded = true;
      state.isFinishedCreating = false;
      state.ondemandSite = action.payload;
    },
    listOndemandRequested: (state, action) => {
      const product = action.payload?.product;
      if (!product) {
        return;
      }
      if (!state.lists[product]) {
        state.lists[product] = emptyListState();
      }
      state.lists[product].listStatus = 'loading';
      state.lists[product].listError = null;
    },
    listOndemandSucceeded: (state, action) => {
      const { product, sites, nextCursor } = action.payload;
      if (!state.lists[product]) {
        state.lists[product] = emptyListState();
      }
      state.lists[product].sites = sites ?? [];
      state.lists[product].nextCursor = nextCursor ?? null;
      state.lists[product].listStatus = 'succeeded';
      state.lists[product].listError = null;
    },
    listOndemandFailed: (state, action) => {
      const { product, error } = action.payload;
      if (!state.lists[product]) {
        state.lists[product] = emptyListState();
      }
      state.lists[product].listStatus = 'failed';
      state.lists[product].listError = error;
    },
    decommissionOndemandRequested: (state, action) => {
      state.decommission = {
        status: 'loading',
        sk: action.payload?.sk ?? null,
        label: action.payload?.label ?? null,
        error: null,
      };
    },
    decommissionOndemandSucceeded: (state) => {
      state.decommission.status = 'succeeded';
      state.decommission.error = null;
    },
    decommissionOndemandFailed: (state, action) => {
      state.decommission.status = 'failed';
      state.decommission.error = action.payload?.error ?? null;
    },
    decommissionOndemandCleared: (state) => {
      state.decommission = {
        status: 'idle',
        sk: null,
        label: null,
        error: null,
      };
    },
    resetSaveState: (state) => {
      state.isSaving = false;
      state.isFetching = false;
      state.error = null;
      state.succeeded = false;
    }
  }
});

// Actions
export const {
  fetchOndemandRequested,
  fetchOndemandFailed,
  fetchOndemandSucceeded,
  createOndemandRequested,
  createOndemandFailed,
  createOndemandSucceeded,
  listOndemandRequested,
  listOndemandSucceeded,
  listOndemandFailed,
  decommissionOndemandRequested,
  decommissionOndemandSucceeded,
  decommissionOndemandFailed,
  decommissionOndemandCleared,
  resetSaveState,
} = slice.actions;

// Selectors
export const selectIsSaving = (state) => state.ondemand.isSaving;
export const selectSucceeded = (state) => state.ondemand.succeeded;
export const selectError = (state) => state.ondemand.error;
export const selectIsFetching = (state) => state.ondemand.isFetching;
export const selectIsFinishedCreating = (state) => state.ondemand.isFinishedCreating;
export const selectOndemandSite = (state) => state.ondemand.ondemandSite;

export const selectOndemandList = (state, product) =>
  state.ondemand.lists[product] ?? emptyListState();

export const selectOndemandSites = (state, product) =>
  selectOndemandList(state, product).sites;

export const selectOndemandListStatus = (state, product) =>
  selectOndemandList(state, product).listStatus;

export const selectOndemandListError = (state, product) =>
  selectOndemandList(state, product).listError;

const idleDecommission = {
  status: 'idle',
  sk: null,
  label: null,
  error: null,
};

export const selectOndemandDecommission = (state) =>
  state.ondemand.decommission ?? idleDecommission;

export default slice.reducer;
